import { Flags } from '@oclif/core';
import { execSync, spawnSync } from 'node:child_process';
import { existsSync, readFileSync, appendFileSync, writeFileSync, mkdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { BaseCommand } from '../../base-command.js';
import { isAuthenticated } from '../../lib/auth.js';
import { findBinary } from '../../lib/which.js';

const CERT_PATH = join(homedir(), '.mitmproxy', 'mitmproxy-ca-cert.pem');
const CONFIG_DIR = join(homedir(), '.config', 'rulemetric');
const DEFAULT_PORT = 8787;

interface CheckResult {
  name: string;
  ok: boolean;
  message: string;
  fix?: () => Promise<boolean>;
}

export default class ProxySetup extends BaseCommand {
  static override description = 'Set up context capture proxy (installs mitmproxy, configures shell)';

  static override examples = [
    '<%= config.bin %> proxy setup',
    '<%= config.bin %> proxy setup --shell zsh',
    '<%= config.bin %> proxy setup --check',
  ];

  static override flags = {
    shell: Flags.string({
      description: 'Shell to configure (auto-detected if omitted)',
      options: ['zsh', 'bash', 'fish', 'powershell'],
    }),
    port: Flags.integer({
      char: 'p',
      default: DEFAULT_PORT,
      description: 'Proxy listen port',
    }),
    check: Flags.boolean({
      description: 'Only check prerequisites, do not install anything',
      default: false,
    }),
  };

  async run(): Promise<void> {
    const { flags } = await this.parse(ProxySetup);
    const checkOnly = flags.check;
    const port = flags.port;

    this.log('RuleMetric Proxy Setup');
    this.log('=====================\n');

    const checks: CheckResult[] = [
      await this.checkAuth(),
      await this.checkMitmproxy(),
      await this.checkCACert(),
      await this.checkHooks(),
      this.checkShellEnv(port),
    ];

    // Display results
    let allPassed = true;
    for (const check of checks) {
      const icon = check.ok ? '[OK]' : '[!!]';
      this.log(`${icon} ${check.name}: ${check.message}`);
      if (!check.ok) allPassed = false;
    }

    if (allPassed) {
      this.log('\nAll checks passed. Run `rulemetric proxy start` to begin capturing.');
      return;
    }

    if (checkOnly) {
      this.log('\nSome checks failed. Run `rulemetric proxy setup` (without --check) to fix.');
      return;
    }

    // Fix failures
    this.log('\nFixing issues...\n');

    for (const check of checks) {
      if (check.ok || !check.fix) continue;
      this.log(`Fixing: ${check.name}...`);
      const fixed = await check.fix();
      if (fixed) {
        this.log(`  Fixed.\n`);
      } else {
        this.log(`  Could not auto-fix. See instructions above.\n`);
      }
    }

    // Re-check
    this.log('---\nVerifying...\n');
    const reChecks = [
      await this.checkAuth(),
      await this.checkMitmproxy(),
      await this.checkCACert(),
      await this.checkHooks(),
      this.checkShellEnv(port),
    ];

    let finalOk = true;
    for (const check of reChecks) {
      const icon = check.ok ? '[OK]' : '[!!]';
      this.log(`${icon} ${check.name}: ${check.message}`);
      if (!check.ok) finalOk = false;
    }

    if (finalOk) {
      this.log('\nSetup complete! Next steps:');
      this.log('  1. Restart your shell (or run `source ~/.zshrc`)');
      this.log('  2. Run `rulemetric proxy start`');
      this.log('  3. Start Claude Code — context will be captured automatically');
    } else {
      this.log('\nSome issues remain. Fix manually and re-run `rulemetric proxy setup --check`.');
    }
  }

  private async checkAuth(): Promise<CheckResult> {
    // Check JWT auth (auth.json)
    if (isAuthenticated()) {
      return { name: 'Authentication', ok: true, message: 'Logged in (JWT)' };
    }

    // Check API key auth (env vars or env file)
    const apiKey = process.env.RULEMETRIC_API_KEY;
    if (apiKey) {
      return { name: 'Authentication', ok: true, message: `API key (RULEMETRIC_API_KEY=${apiKey.slice(0, 8)}...)` };
    }

    // Check env file
    const envFile = join(homedir(), '.config', 'rulemetric', 'env');
    if (existsSync(envFile)) {
      const content = readFileSync(envFile, 'utf-8');
      if (content.includes('RULEMETRIC_API_KEY') || content.includes('RULEMETRIC_ACCESS_TOKEN')) {
        return { name: 'Authentication', ok: true, message: 'Configured via ~/.config/rulemetric/env' };
      }
    }

    return {
      name: 'Authentication',
      ok: false,
      message: 'Not authenticated',
      fix: async () => {
        this.log('  Run `rulemetric auth login` to authenticate.');
        this.log('  Or set RULEMETRIC_API_KEY in ~/.config/rulemetric/env.');
        return false;
      },
    };
  }

  private async checkMitmproxy(): Promise<CheckResult> {
    const binary = this.findBinary('mitmdump');
    if (binary) {
      // Get version
      try {
        const output = execSync(`${binary} --version 2>&1`, { encoding: 'utf-8' });
        const version = output.match(/Mitmproxy:\s*([\d.]+)/)?.[1] ?? 'unknown';
        return { name: 'mitmproxy', ok: true, message: `Installed (v${version})` };
      } catch {
        return { name: 'mitmproxy', ok: true, message: `Found at ${binary}` };
      }
    }

    return {
      name: 'mitmproxy',
      ok: false,
      message: 'Not installed',
      fix: async () => this.installMitmproxy(),
    };
  }

  private async installMitmproxy(): Promise<boolean> {
    // Try pipx first (cleanest isolation)
    if (this.findBinary('pipx')) {
      this.log('  Installing via pipx...');
      const result = spawnSync('pipx', ['install', 'mitmproxy'], { stdio: 'inherit' });
      if (result.status === 0) return true;
    }

    // Try uv tool install
    if (this.findBinary('uv')) {
      this.log('  Installing via uv...');
      const result = spawnSync('uv', ['tool', 'install', 'mitmproxy'], { stdio: 'inherit' });
      if (result.status === 0) return true;
    }

    // Try pip with --user
    if (this.findBinary('pip3') || this.findBinary('pip')) {
      const pip = this.findBinary('pip3') || this.findBinary('pip')!;
      this.log(`  Installing via ${pip} --user...`);
      const result = spawnSync(pip, ['install', '--user', 'mitmproxy'], { stdio: 'inherit' });
      if (result.status === 0) return true;
    }

    // Try brew
    if (this.findBinary('brew')) {
      this.log('  Installing via brew...');
      const result = spawnSync('brew', ['install', 'mitmproxy'], { stdio: 'inherit' });
      if (result.status === 0) return true;
    }

    this.log('  Could not auto-install mitmproxy. Install manually:');
    this.log('    pipx install mitmproxy     # recommended');
    this.log('    brew install mitmproxy     # macOS');
    this.log('    pip install --user mitmproxy');
    return false;
  }

  private async checkCACert(): Promise<CheckResult> {
    if (existsSync(CERT_PATH)) {
      return { name: 'CA Certificate', ok: true, message: `Found at ${CERT_PATH}` };
    }

    return {
      name: 'CA Certificate',
      ok: false,
      message: 'Not generated yet',
      fix: async () => this.generateCACert(),
    };
  }

  private async generateCACert(): Promise<boolean> {
    const mitmdump = this.findBinary('mitmdump');
    if (!mitmdump) {
      this.log('  Cannot generate cert — mitmproxy not installed.');
      return false;
    }

    // Run mitmdump briefly to generate certs
    this.log('  Generating CA certificate...');
    const result = spawnSync(mitmdump, ['--set', 'listen_port=0', '-q'], {
      timeout: 5000,
      stdio: 'ignore',
    });

    // mitmdump will timeout (expected) — certs should be generated
    if (existsSync(CERT_PATH)) {
      this.log(`  Certificate generated at ${CERT_PATH}`);

      // On macOS, offer to trust the cert
      if (process.platform === 'darwin') {
        this.log('  Adding CA cert to macOS keychain (may require sudo)...');
        const trustResult = spawnSync('sudo', [
          'security', 'add-trusted-cert', '-d',
          '-r', 'trustRoot',
          '-k', '/Library/Keychains/System.keychain',
          CERT_PATH,
        ], { stdio: 'inherit' });
        if (trustResult.status === 0) {
          this.log('  CA cert trusted system-wide.');
        } else {
          this.log('  Could not auto-trust cert. You may need NODE_EXTRA_CA_CERTS env var.');
        }
      }

      return true;
    }

    this.log('  Failed to generate cert. Try running: mitmdump --help');
    return false;
  }

  private async checkHooks(): Promise<CheckResult> {
    // Hooks may live in settings.local.json (current installs write
    // machine-local state there) or the shared settings.json (legacy
    // installs) — either file counts.
    let sawSettingsFile = false;
    for (const fileName of ['settings.local.json', 'settings.json']) {
      const settingsPath = join(process.cwd(), '.claude', fileName);
      if (!existsSync(settingsPath)) continue;
      sawSettingsFile = true;
      try {
        const settings = JSON.parse(readFileSync(settingsPath, 'utf-8'));
        const hasHooks = settings.hooks && Object.keys(settings.hooks).length > 0;
        if (hasHooks) {
          return { name: 'Session Hooks', ok: true, message: 'Installed in current project' };
        }
      } catch { /* fall through */ }
    }

    if (!sawSettingsFile) {
      return {
        name: 'Session Hooks',
        ok: false,
        message: 'Not installed in current project',
        fix: async () => {
          this.log('  Run `rulemetric hooks install` in your project directory.');
          return false; // User needs to run in the right directory
        },
      };
    }

    return {
      name: 'Session Hooks',
      ok: false,
      message: 'settings file exists but hooks not configured',
      fix: async () => {
        this.log('  Run `rulemetric hooks install` to configure hooks.');
        return false;
      },
    };
  }

  private checkShellEnv(port: number): CheckResult {
    const proxySet = process.env.HTTPS_PROXY?.includes(String(port));
    const certSet = !!process.env.NODE_EXTRA_CA_CERTS;

    if (proxySet && certSet) {
      return { name: 'Shell Environment', ok: true, message: 'HTTPS_PROXY and NODE_EXTRA_CA_CERTS set' };
    }

    const missing: string[] = [];
    if (!proxySet) missing.push('HTTPS_PROXY');
    if (!certSet) missing.push('NODE_EXTRA_CA_CERTS');

    return {
      name: 'Shell Environment',
      ok: false,
      message: `Missing: ${missing.join(', ')}`,
      fix: async () => this.writeShellEnv(port),
    };
  }

  private async writeShellEnv(port: number): Promise<boolean> {
    const shell = this.detectShell();
    const rcFile = this.getShellRcFile(shell);

    if (!rcFile) {
      this.log(`  Could not detect shell RC file. Add these to your shell profile:`);
      this.log(`    export HTTPS_PROXY=http://localhost:${port}`);
      this.log(`    export NODE_EXTRA_CA_CERTS=${CERT_PATH}`);
      return false;
    }

    const oldMarker = '# RuleMetric proxy';
    const marker = '# RuleMetric proxy (auto-detect)';
    const envFile = join(CONFIG_DIR, 'proxy.env');
    const pidFile = join(CONFIG_DIR, 'proxy.pid');

    // The sentinel proves the block is already installed (idempotency). POSIX
    // shells share one function name; PowerShell uses its own.
    const sentinel = shell === 'powershell' ? '_RuleMetricProxyCheck' : '_rulemetric_proxy_check';

    // Dynamic proxy env: re-evaluates on every prompt so stopping the proxy
    // immediately unsets HTTPS_PROXY without needing a new shell.
    const block = shell === 'powershell'
      ? this.buildPowerShellBlock(marker, pidFile, port)
      : shell === 'fish'
      ? [
          '',
          marker,
          'function _rulemetric_proxy_check',
          `  if test -f ${pidFile}; and kill -0 (cat ${pidFile}) 2>/dev/null`,
          `    source ${envFile} 2>/dev/null`,
          '  else',
          '    set -e HTTPS_PROXY 2>/dev/null',
          '    set -e NODE_EXTRA_CA_CERTS 2>/dev/null',
          '  end',
          'end',
          '',
        ].join('\n')
      : shell === 'zsh'
      ? [
          '',
          marker,
          '_rulemetric_proxy_check() {',
          `  if [ -f "${pidFile}" ] && kill -0 "$(cat "${pidFile}" 2>/dev/null)" 2>/dev/null; then`,
          `    [ -z "\${HTTPS_PROXY:-}" ] && source "${envFile}" 2>/dev/null`,
          '  else',
          `    [ -n "\${HTTPS_PROXY:-}" ] && unset HTTPS_PROXY NODE_EXTRA_CA_CERTS`,
          '  fi',
          '}',
          'precmd_functions+=(_rulemetric_proxy_check)',
          '',
        ].join('\n')
      : [
          '',
          marker,
          '_rulemetric_proxy_check() {',
          `  if [ -f "${pidFile}" ] && kill -0 "$(cat "${pidFile}" 2>/dev/null)" 2>/dev/null; then`,
          `    [ -z "\${HTTPS_PROXY:-}" ] && source "${envFile}" 2>/dev/null`,
          '  else',
          `    [ -n "\${HTTPS_PROXY:-}" ] && unset HTTPS_PROXY NODE_EXTRA_CA_CERTS`,
          '  fi',
          '}',
          'PROMPT_COMMAND="_rulemetric_proxy_check${PROMPT_COMMAND:+;$PROMPT_COMMAND}"',
          '',
        ].join('\n');

    // The PowerShell `$PROFILE` directory frequently does not exist yet; create
    // it (and any other missing rc dir, e.g. fish) before appending.
    mkdirSync(dirname(rcFile), { recursive: true });

    if (existsSync(rcFile)) {
      let content = readFileSync(rcFile, 'utf-8');

      // Remove old static block if present
      if (content.includes(oldMarker) && !content.includes(marker)) {
        content = content.replace(
          /\n# RuleMetric proxy\nexport HTTPS_PROXY=[^\n]*\nexport NODE_EXTRA_CA_CERTS=[^\n]*\n/,
          '\n',
        );
        writeFileSync(rcFile, content);
        this.log(`  Removed old static env vars from ${rcFile}`);
      }

      // Remove old auto-detect block (the one without precmd that doesn't unset on stop)
      if (content.includes('# RuleMetric proxy (auto-detect') && !content.includes('_rulemetric_proxy_check')) {
        content = content.replace(
          /\n# RuleMetric proxy \(auto-detect[^\n]*\)\nif \[.*?\nfi\n/s,
          '\n',
        );
        writeFileSync(rcFile, content);
        this.log(`  Upgraded old auto-detect block in ${rcFile}`);
      }

      if (content.includes(sentinel)) {
        this.log(`  Shell env already configured in ${rcFile}`);
        return true;
      }
    }

    this.log(`  Appending proxy auto-detect hook to ${rcFile}`);
    this.log(`  (HTTPS_PROXY is set/unset automatically when proxy starts/stops)`);
    appendFileSync(rcFile, block);
    return true;
  }

  private detectShell(): string {
    // On Windows there is no $SHELL and PowerShell is the de-facto shell.
    if (process.platform === 'win32') return 'powershell';
    const shell = process.env.SHELL || '';
    if (shell.includes('zsh')) return 'zsh';
    if (shell.includes('bash')) return 'bash';
    if (shell.includes('fish')) return 'fish';
    return 'zsh'; // default on macOS
  }

  private getShellRcFile(shell: string): string | null {
    const home = homedir();
    switch (shell) {
      case 'zsh': return join(home, '.zshrc');
      case 'bash': return join(home, '.bashrc');
      case 'fish': return join(home, '.config', 'fish', 'config.fish');
      case 'powershell': return this.getPowerShellProfilePath();
      default: return null;
    }
  }

  /**
   * Resolve the PowerShell profile that loads for every host (console, VS Code
   * integrated terminal, etc.). We ask PowerShell itself for the canonical path
   * — guessing `~/Documents/...` is wrong when Documents is OneDrive-redirected.
   * Falls back to the PowerShell 7 default only if no PS binary is reachable.
   */
  private getPowerShellProfilePath(): string {
    const psBin = findBinary('pwsh') || findBinary('powershell');
    if (psBin) {
      try {
        const out = execSync(`"${psBin}" -NoProfile -Command "$PROFILE.CurrentUserAllHosts"`, {
          encoding: 'utf-8',
        }).trim();
        if (out) return out;
      } catch {
        /* fall through to constructed default */
      }
    }
    return join(homedir(), 'Documents', 'PowerShell', 'profile.ps1');
  }

  /**
   * Self-contained PowerShell prompt hook. Unlike the POSIX blocks it does NOT
   * source `proxy.env` (that file is `export KEY=val` syntax PowerShell can't
   * read) — it embeds the values directly. Each prompt it sets HTTPS_PROXY while
   * the proxy PID is alive and removes the vars once it stops, so a dead proxy
   * never leaves a dangling HTTPS_PROXY that would break Claude Code.
   */
  private buildPowerShellBlock(marker: string, pidFile: string, port: number): string {
    const proxyUrl = `http://localhost:${port}`;
    const psPid = pidFile.replace(/'/g, "''");
    const psCert = CERT_PATH.replace(/'/g, "''");
    return [
      '',
      marker,
      'function _RuleMetricProxyCheck {',
      `  $pidFile = '${psPid}'`,
      '  $alive = $false',
      '  if (Test-Path $pidFile) {',
      '    $procId = (Get-Content $pidFile -ErrorAction SilentlyContinue | Select-Object -First 1)',
      '    if ($procId -match \'^\\d+$\' -and (Get-Process -Id ([int]$procId) -ErrorAction SilentlyContinue)) {',
      '      $alive = $true',
      '    }',
      '  }',
      '  if ($alive) {',
      `    $env:HTTPS_PROXY = '${proxyUrl}'`,
      `    $env:HTTP_PROXY = '${proxyUrl}'`,
      `    $env:NODE_EXTRA_CA_CERTS = '${psCert}'`,
      '  } else {',
      '    Remove-Item Env:HTTPS_PROXY, Env:HTTP_PROXY, Env:NODE_EXTRA_CA_CERTS -ErrorAction SilentlyContinue',
      '  }',
      '}',
      '# Wrap the existing prompt once so the check runs every prompt.',
      'if (-not (Test-Path Function:\\_RuleMetricOriginalPrompt)) {',
      '  $existing = Get-Command prompt -ErrorAction SilentlyContinue',
      '  if ($existing) { Set-Item Function:\\_RuleMetricOriginalPrompt $existing.ScriptBlock }',
      '}',
      'function prompt {',
      '  _RuleMetricProxyCheck',
      '  if (Test-Path Function:\\_RuleMetricOriginalPrompt) { & _RuleMetricOriginalPrompt } else { "PS $($PWD.Path)> " }',
      '}',
      '',
    ].join('\n');
  }

  private findBinary(name: string): string | null {
    // Portable PATH scan — `which` does not exist on Windows.
    return findBinary(name);
  }
}
